import React, { useState } from 'react';
import { BooleanView, getTableColumn, Select, TableActionIcon, TableColumn } from '@shared/UI';
import { ActionStatuses, ISettingsAccountsDto, MarginTypes } from '@shared/api';
import { FaLink, FaXmark, FaCheck } from 'react-icons/fa6';
import { AccountAction, useAccountsTable } from './';
import { FilterArrowsIcon } from '@shared/icons/FilterArrowsIcon';
import { IconType } from 'react-icons';
import { getHeaderClassName } from '@shared/UI/SimpleTable/lib/getHeaderClassName';
import { HeaderCellWithFilter } from '@shared/UI/SimpleTable/components/HeaderCellWithFilter';
import { useLookups } from '@shared/providers/LookupsProvider';
import { mapLookupsToSelectOptions } from '@shared/utils';
import { KYCRequests } from '@widgets/AccountsTable/components/KYCRequests';

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;

interface IOptions {
    onAccountActionClick: (
        account: ISettingsAccountsDto,
        action: AccountAction,
        refresh: () => void,
        AdditionalComponent?: React.FC | null,
    ) => void;
}

const ACTION_BUTTONS = [
    {
        title: 'approve',
        icon: FaCheck,
        allowedAccountStatuses: [1, 2],
    },
    {
        title: 'kyc',
        icon: FaLink,
        disabled: true,
        allowedAccountStatuses: [1, 2, 3, 5, 6],
    },
    {
        title: 'reject',
        icon: FaXmark,
        allowedAccountStatuses: [1, 2],
    },
];

export function getAccountsTableColumns(options: IOptions): TableColumn<ISettingsAccountsDto>[] {
    const {
        accountStatuses,
        accountMarginTypes,
        desc,
        isError,
        isInventory,
        sortFieldName,
        accountsRefresh,
        setAccountStatuses,
        setAccountMarginTypes,
        handleAccountsSort,
        setIsInventory,
        setIsError,
    } = useAccountsTable();
    const { onAccountActionClick } = options;

    const { lookups } = useLookups();
    // FIXME
    // bad crutch for using env
    // problem: we are using envs by replacing strings in final build
    // so we cant use it normally in conditions because this part is removing as useless after compile
    const [environment, setEnvironment] = useState(ENVIRONMENT);
    const accountStatusOptions = lookups && mapLookupsToSelectOptions('AccountStatus', lookups);
    const accountMarginTypeOptions = lookups && mapLookupsToSelectOptions('AccountMarginType', lookups);

    const isErrorOptions = [
        { label: 'Is Error', value: 'true' },
        { label: 'Not Error', value: 'false' },
    ];

    const isInventoryOptions = [
        { label: 'Is Inventory', value: 'true' },
        { label: 'Not Inventory', value: 'false' },
    ];

    return [
        getTableColumn({
            dataField: 'clearingAccount',
            flex: 1.46,
            headerFormatter: () => (
                <HeaderCellWithFilter
                    title='Account Number'
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handleAccountsSort('clearingAccount')}
                />
            ),
            headerClassName: `accounts-table-header ${getHeaderClassName(sortFieldName, desc, 'clearingAccount')}`,
        }),
        getTableColumn({
            dataField: 'fdid',
            flex: 1.46,
        }),
        getTableColumn({
            // dataField: 'verificationRequest.firstName',
            flex: 1.2,
            title: 'Name',
            formatter: (_, row) => {
                if (row.customerType === 'Entity') {
                    return row.verificationRequest?.entityName;
                }
                return `${row.verificationRequest?.firstName} ${row.verificationRequest?.lastName}`;
            },
            headerClassName: `accounts-table-header`,
        }),
        getTableColumn({
            dataField: 'customerType',
            flex: 1.3,
            headerClassName: `accounts-table-header ${getHeaderClassName(sortFieldName, desc, 'fdid')}`,
        }),
        getTableColumn({
            dataField: 'cash',
            flex: 1.3,
            lastColumn: true,
            formatter: (cell) => `$${cell}`,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handleAccountsSort('cash')}
                />
            ),
            headerClassName: `accounts-table-header ${getHeaderClassName(sortFieldName, desc, 'cash')}`,
        }),
        getTableColumn({
            dataField: 'baseCash',
            flex: 1.3,
            lastColumn: true,
            formatter: (cell) => `$${cell}`,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handleAccountsSort('baseCash')}
                />
            ),
            headerClassName: `accounts-table-header ${getHeaderClassName(sortFieldName, desc, 'baseCash')}`,
        }),
        getTableColumn({
            dataField: 'status',
            flex: 1.3,
            formatter: (cell) => ActionStatuses[cell as number],
            headerFormatter: () => (
                <Select
                    currentValue={accountStatuses}
                    options={accountStatusOptions || []}
                    dropdownPlacement='bottom-right'
                    placeholder='Status'
                    multiple
                    nonInputView
                    withClearButton
                    onSelect={setAccountStatuses}
                />
            ),
        }),
        getTableColumn({
            dataField: 'marginType',
            flex: 1.3,
            formatter: (cell) => MarginTypes[cell as number],
            headerFormatter: () => (
                <Select
                    currentValue={accountMarginTypes}
                    options={accountMarginTypeOptions || []}
                    dropdownPlacement='bottom-right'
                    placeholder='Type'
                    multiple
                    nonInputView
                    withClearButton
                    onSelect={setAccountMarginTypes}
                />
            ),
        }),
        getTableColumn({
            dataField: 'isError',
            flex: 1,
            formatter: (data) => <BooleanView is={!!data} />,
            headerFormatter: () => (
                <Select
                    currentValue={isError}
                    options={isErrorOptions}
                    dropdownPlacement='bottom-right'
                    placeholder='Error'
                    nonInputView
                    withClearButton
                    onSelect={setIsError}
                />
            ),
        }),
        getTableColumn({
            dataField: 'isInventory',
            flex: 1,
            formatter: (data) => <BooleanView is={!!data} />,
            headerFormatter: () => (
                <Select
                    currentValue={isInventory}
                    options={isInventoryOptions}
                    dropdownPlacement='bottom-right'
                    placeholder='Inventory'
                    nonInputView
                    withClearButton
                    onSelect={setIsInventory}
                />
            ),
        }),
        getTableColumn({
            flex: 0.9,
            formatter: (_, row) => {
                const isIndividualCustomerType = row.customerType === 'Individual';
                return (
                    <>
                        {ACTION_BUTTONS.map(
                            (actionButton) =>
                                actionButton.allowedAccountStatuses.includes(row.status) &&
                                (actionButton.title === 'kyc' ? isIndividualCustomerType : true) && (
                                    <TableActionIcon
                                        key={actionButton.title}
                                        isActive={environment === 'production' ? true : !actionButton.disabled}
                                        className='accounts-actions-button'
                                        onClick={() =>
                                            onAccountActionClick(
                                                row,
                                                actionButton.title as AccountAction,
                                                accountsRefresh,
                                                actionButton.title === 'kyc'
                                                    ? () => <KYCRequests clearingAccount={row.clearingAccount} />
                                                    : null,
                                            )
                                        }
                                        Icon={actionButton.icon}
                                    />
                                ),
                        )}
                    </>
                );
            },
        }),
    ];
}
